import type { Updaters } from '~/adapter/indexedDB/types'
import type { OfflineDownloadInfoSchemaV5 } from '../../5/downloadInfo/downloadInfo'
import type { EmployeeSchemaV5 } from '../../5/employee/employee'
import type { FeatureFlagSchemaV5 } from '../../5/featureFlag/featureFlag'
import type {
  MultipleChoiceSetSchemaV5,
  TemplateMultipleChoiceSetRelationSchemaV5,
} from '../../5/multipleChoiceSet/multipleChoiceSet'
import type {
  ReportManualSchemaV5,
  ReportNodeSchemaV5,
  ReportSchemaV5,
} from '../../5/report/report'
import type { ScheduleItemSchemaV5 } from '../../5/schedule/scheduleItem'
import type {
  TemplateNodeSchemaV5,
  TemplateSchemaV5,
} from '../../5/template/template'
import type { TemplateMediaSchemaV5 } from '../../5/template/templateMedia'
import type { VersionSchemaV5 } from '../../5/version/version'
import type { OfflineDownloadInfoSchemaV6 } from '../downloadInfo/downloadInfo'
import type { EmployeeSchemaV6 } from '../employee/employee'
import type { FeatureFlagSchemaV6 } from '../featureFlag/featureFlag'
import type {
  MultipleChoiceSetSchemaV6,
  TemplateMultipleChoiceSetRelationSchemaV6,
} from '../multipleChoiceSet/multipleChoiceSet'
import type {
  ReportManualSchemaV6,
  ReportNodeSchemaV6,
  ReportSchemaV6,
} from '../report/report'
import type { ReportDataV6 } from '../report/reportMode/reportMode'
import type { ScheduleItemSchemaV6 } from '../schedule/scheduleItem'
import type {
  TemplateNodeSchemaV6,
  TemplateSchemaV6,
} from '../template/template'
import type { TemplateMediaSchemaV6 } from '../template/templateMedia'
import type { VersionSchemaV6 } from '../version/version'

const version = 6
type versionType = typeof version

export const migration: Pick<
  Updaters[versionType],
  'upgradeBefore' | 'migrations'
> = {
  upgradeBefore: async () => {},
  migrations: {
    versions: (prev: VersionSchemaV5): VersionSchemaV6 => {
      return {
        ...prev,
        // NOTICE: version テーブルのインクリメントを忘れないこと
        version,
      }
    },
    featureFlags: (prev: FeatureFlagSchemaV5): FeatureFlagSchemaV6 => {
      return prev
    },
    // 記録者 (assignee) 関連フィールドを永続データから除去する。
    // - new-report 系: reportMode.reportAssignee を除去
    // - editReport: reportMode.report.assignee を除去
    reports: (prev: ReportSchemaV5): ReportSchemaV6 => {
      const { reportMode } = prev.data
      if (reportMode.modeType === 'editReport') {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { assignee, ...report } = reportMode.report
        const nextReport: ReportDataV6 = report
        return {
          ...prev,
          data: {
            ...prev.data,
            reportMode: { ...reportMode, report: nextReport },
          },
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { reportAssignee, ...restReportMode } = reportMode
      return {
        ...prev,
        data: {
          ...prev.data,
          reportMode: restReportMode,
        },
      }
    },
    reportNodes: (prev: ReportNodeSchemaV5): ReportNodeSchemaV6 => {
      return prev
    },
    reportManuals: (prev: ReportManualSchemaV5): ReportManualSchemaV6 => {
      return prev
    },
    employees: (prev: EmployeeSchemaV5): EmployeeSchemaV6 => {
      return prev
    },
    offlineDownloadInfos: (
      prev: OfflineDownloadInfoSchemaV5,
    ): OfflineDownloadInfoSchemaV6 => {
      return prev
    },
    scheduleItems: (prev: ScheduleItemSchemaV5): ScheduleItemSchemaV6 => {
      return prev
    },
    templates: (prev: TemplateSchemaV5): TemplateSchemaV6 => {
      return prev
    },
    templateNodes: (prev: TemplateNodeSchemaV5): TemplateNodeSchemaV6 => {
      return prev
    },
    templateMedia: (prev: TemplateMediaSchemaV5): TemplateMediaSchemaV6 => {
      return prev
    },
    multipleChoiceSets: (
      prev: MultipleChoiceSetSchemaV5,
    ): MultipleChoiceSetSchemaV6 => {
      return prev
    },
    templateMultipleChoiceSetRelations: (
      prev: TemplateMultipleChoiceSetRelationSchemaV5,
    ): TemplateMultipleChoiceSetRelationSchemaV6 => {
      return prev
    },
  },
}
